import { api } from '../axios'
import type { ApiResponse } from '../../../types'

export interface Grievance {
  id: number
  subject: string
  description: string
  status: 'submitted' | 'under_review' | 'replied' | 'closed' | 'rejected'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  category: string
  recipient_type: 'DIRECTORATE' | 'SECRETARIAT'
  created_at: string
  updated_at?: string
  teacher?: {
    id: number
    teacher_name: string
    teacher_ID: string
    email: string
    phone_number: string
    district: string
    rd_block: string
    current_school_name: string
  }
  replies?: Array<{
    id: number
    reply_text: string
    is_internal: boolean
    created_at: string
    user?: {
      id: number
      username: string
      role: string
    }
  }>
}

export interface GrievanceFilters {
  status?: string
  priority?: string
  category?: string
  search?: string
  recipient_type?: 'DIRECTORATE' | 'SECRETARIAT'
  page?: number
  limit?: number
}

export const grievancesApi = {
  // Get admin grievances
  getAdminGrievances: (filters: GrievanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.status) params.append('status', filters.status)
    if (filters.priority) params.append('priority', filters.priority)
    if (filters.category) params.append('category', filters.category)
    if (filters.search) params.append('search', filters.search)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    
    return api.get<ApiResponse<Grievance[]> & { pagination?: any }>(`/grievances/admin/list?${params.toString()}`)
  },

  // Get count of new/pending grievances for current user
  // Counts grievances with status 'submitted' (new submissions)
  // The backend automatically determines recipient_type based on user role
  getPendingCount: () => {
    const params = new URLSearchParams()
    params.append('status', 'submitted')
    params.append('page', '1')
    params.append('limit', '1')
    
    return api.get<ApiResponse<Grievance[]> & { pagination?: { total: number } }>(
      `/grievances/admin/list?${params.toString()}`
    )
  },

  // Get grievance by ID
  getById: (id: number) =>
    api.get<ApiResponse<Grievance>>(`/grievances/${id}`),

  // Reply to a grievance
  reply: (id: number, data: { reply_text: string; is_internal?: boolean }) =>
    api.post<ApiResponse>(`/grievances/${id}/reply`, data),

  // Update grievance status
  updateStatus: (id: number, data: { status: string; priority?: string }) =>
    api.patch<ApiResponse>(`/grievances/${id}/status`, data),

  // Get grievance stats
  getStats: () =>
    api.get<ApiResponse<any>>('/grievances/admin/stats'),

  // Close a grievance
  close: (id: number) =>
    api.patch<ApiResponse>(`/grievances/${id}/close`, {})
}
